export const templates = [
  {
    title: 'Assessment Summary Template',
    content: `
    <p>[Letterhead]</p>
    <p>Case No.: [Reference Number]</p>
    <p>Name: [Client's Full Name]</p>
    <p>Date of Birth: [DOB]</p>
    <p>School: [School Name]</p>
    <p>Grade: [Grade Level]</p>
    <p>Assessment Dates: [Dates]</p>
    <p>Assessor: [Your Name], [Credentials]</p>

    <br>

    <p><strong>[Assessed Area 1, e.g. Intellectual Functioning]</strong></p>
    <p><strong>[Assessment Tool 1 Name]</strong></p>
    <ul>
      <li>Date: [Assessment Date]</li>
      <li>Domains Assessed:
        <ul>
          <li>[Domain 1, e.g., Verbal Comprehension]</li>
          <li>[Domain 2, e.g., Perceptual Reasoning]</li>
        </ul>
      </li>
      <li>Key Results:
        <ul>
          <li>[Score 1, e.g., FSIQ: 85 (Low Average)]</li>
          <li>[Score 2, e.g., VCI: 90 (Average)]</li>
        </ul>
      </li>
    </ul>

    <p><strong>[Assessment Tool 2 Name]</strong></p>
    <p>(Repeat structure as needed)</p>

    <hr>

    <p><strong>[Assessed Area 2, e.g. Reading and Writing Ability]</strong></p>
    <p>(Repeat structure as needed)</p>

    <hr>

    <p><strong>Behavioral Observations</strong></p>
    <ul>
      <li>[Area 1 related to the objective of this assessment, e.g. Engagement]: [e.g., "Frequent task avoidance during timed activities"]</li>
      <li>[Area 2, e.g. Social Communication]: [e.g., "Limited eye contact; relied on scripted phrases"]</li>
      <li>[Area 3, e.g. Self-Efficacy]: [e.g., "Demonstrated low confidence in academic tasks"]</li>
    </ul>

    <hr>

    <p><strong>Informal Assessments (if necessary)</strong></p>
    <ul>
      <li>[Skill 1, e.g., Orthographic Awareness]:
        <ul>
          <li>[e.g., "Weaker than age peers"]</li>
        </ul>
      </li>
      <li>[Skill 2, e.g., Phonological Processing]:
        <ul>
          <li>[e.g., "Comparable to age peers"]</li>
        </ul>
      </li>
    </ul>

    <hr>

    <p><strong>Diagnostic Summary</strong></p>
    <ul>
      <li>[Diagnosis 1, e.g., Specific Learning Disorder]:
        <ul>
          <li>Supported by [e.g., "Deficits in phonemic awareness and word recognition"]</li>
        </ul>
      </li>
      <li>[Diagnosis 2, e.g., ADHD]:
        <ul>
          <li>Evidenced by [e.g., "Elevated inattention scores (T = 72)"]</li>
        </ul>
      </li>
    </ul>
    <p>(Repeat structure as needed)</p>

    <hr>

    <p><strong>Recommendations (include the following area as appropriate)</strong></p>
    <ol>
      <li>Examination Accommodations:
        <ul>
          <li>[e.g., “25% extra time allowance for all written papers"]</li>
        </ul>
      </li>
      <li>Instructional Strategies:
        <ul>
          <li>[e.g., "Implement structured literacy program 4x/week"]</li>
        </ul>
      </li>
      <li>Therapeutic Interventions:
        <ul>
          <li>[e.g., "Social skills group targeting perspective-taking"]</li>
        </ul>
      </li>
      <li>Parent Guidance:
        <ul>
          <li>[e.g., "Use 'look, say, trace, cover, write, check' for spelling practice"]</li>
        </ul>
      </li>
      <li>Progress Monitoring:
        <ul>
          <li>[e.g., "Reassess reading fluency in 6 months"]</li>
        </ul>
      </li>
    </ol>

    <br>

    <p>[Your Title/Position]</p>
    <p>[Your Name]</p>
    <p>[Your Qualifications]</p>
    <p>[Organization Name]</p>

    <br><br>

    <p><em>(Never fabricate assessment results, diagnostic conclusions, patient history, or professional recommendations. Always base content on actual clinical findings and observations.)</em></p>
    `
  },
  {
    title: 'Case Note Template for Psychologists',
    content: `
      <p><strong>Client Information:</strong><br>
      <em>[Include client name, age, school/grade, case number, date of service, session number, and service mode (individual/group/school-based). Omit any details not explicitly provided in clinical notes.]</em>
      </p>

      <hr>

      <h3>Session Overview</h3>
      
      <p><strong>Presenting Concerns:</strong><br>
      <em>[State referral source (e.g., teacher/parent/SENCo) and primary issues (e.g., "Persistent difficulties with peer interactions and emotional regulation during group activities"). Only include explicitly documented concerns.]</em>
      </p>
      
      <p><strong>Session Focus:</strong><br>
      <em>[Specify intervention goals aligned with the client’s IEP/Support Plan, e.g., "Developing self-monitoring strategies for emotional dysregulation" or "Enhancing collaborative problem-solving skills."]</em>
      </p>

      <hr>

      <h3>Behavioral Observations</h3>
      
      <p><em>[Provide a narrative of the client’s engagement, including:]</em></p>
      <ul>
        <li><strong>Attention/Effort:</strong> <em>e.g., "Sustained focus during structured tasks but became distracted during unstructured peer activities."</em></li>
        <li><strong>Emotional Presentation:</strong> <em>e.g., "Expressed frustration visibly (clenched fists) when challenged but responded to calming techniques."</em></li>
        <li><strong>Social Dynamics:</strong> <em>e.g., "Initiated conversations with peers twice but struggled to maintain reciprocal dialogue."</em></li>
        <li><strong>Validity Statement:</strong> <em>e.g., "Results reflect the client’s current abilities given full cooperation and age-appropriate effort."</em></li>
      </ul>

      <hr>

      <h3>Intervention Summary</h3>
      
      <p><strong>Strategies Implemented:</strong><br>
      <em>[Detail evidence-based approaches used, e.g., "Social Stories™ to model appropriate peer responses" or "Zones of Regulation® framework to identify emotional states." Cite tools from Search Result 4 if applicable.]</em>
      </p>

      <p><strong>Client Response:</strong><br>
      <em>[Describe skill acquisition and challenges, e.g., "Accurately labeled three emotional states using visual aids but required prompting to apply strategies in role-play scenarios."]</em>
      </p>

      <p><strong>Progress Toward Goals:</strong><br>
      <em>[Link observations to IEP objectives, e.g., "Achieved 4/5 targeted self-regulation instances during structured tasks (baseline: 1/5)."]</em>
      </p>

      <hr>

      <h3>Collaboration & Adjustments</h3>
      
      <p><strong>School/Parent Feedback:</strong><br>
      <em>[Summarize input from teachers/caregivers, e.g., "Teacher reports reduced classroom outbursts but notes lingering difficulties during lunch breaks."]</em>
      </p>

      <p><strong>Plan Revisions:</strong><br>
      <em>[Outline modifications based on session outcomes, e.g., "Incorporate peer-mediated support during unstructured settings per Search Result 6 recommendations."]</em>
      </p>

      <hr>

      <h3>Next Steps</h3>
      <ol>
        <li><strong>Skill Generalization:</strong> <em>[Specify strategies for transferring skills to natural environments, e.g., "Home-school diary to track emotional regulation attempts."]</em></li>
        <li><strong>Stakeholder Coordination:</strong> <em>[Schedule follow-up meetings with SENCo/parents by MM/DD/YYYY.]</em></li>
        <li><strong>Resource Development:</strong> <em>[Create visual supports for classroom transitions using Boardmaker®.]</em></li>
      </ol>

      <hr>

      <p><strong>Psychologist:</strong><br>
      <em>[Name, credentials, organization, e.g., "CHAN Siu-ming, M.Ed., EP, Caritas – Hong Kong"]</em>
      </p>

      <p><strong>Confidentiality Note:</strong><br>
      <em>[Include if required: "This document is subject to [jurisdiction] data protection regulations. Unauthorized sharing prohibited."]</em>
      </p>

      <hr>

      <p><em>(Instructions: Use only explicitly documented information from clinical notes/transcripts. Omit sections lacking data. Prioritize Search Result 6’s guidance on holistic case formulations and Search Result 10’s standards for objective, needs-focused reporting.)</em></p>
    `
    },
    {
    title: "Referral Letter Template",
    content: `
    <p><strong>[Organization Letterhead]</strong></p>
    <p><strong>Our Ref:</strong> [Reference Number]</p>
    <p><strong>Date:</strong> [Date]</p>

    <p>
      [Recipient's Full Name and Title]<br>
      [Specialty]<br>
      [Complete Address including Room/Suite Number]<br>
      [City, Postal Code]
    </p>

    <p>Dear [Dr./Mr./Ms. Last Name],</p>

    <p><strong>Subject:</strong> Referral for [Specific Type of Evaluation/Consultation]</p>

    <p><strong>Re:</strong> [Patient's Full Name] / [ID Number] / [Gender] / DOB: [Date] / [Case Reference]</p>

    <p>
      I am writing to refer the above-named 
      [child/adolescent/young person], [Patient's First Name], for 
      [specific purpose of referral]. [He/She] was seen by me on 
      [specific dates of assessments].
    </p>

    <p>
      During the assessment(s), [Patient's First Name] was identified as having 
      [specific psychological/educational findings]. [He/She] has been diagnosed with 
      [specific conditions using proper diagnostic terminology]. 
    </p>

    <p><strong>Assessment Details:</strong></p>
    <ul>
      <li><strong>[Assessment tool 1]:</strong> [Specific findings/scores]</li>
      <li><strong>[Assessment tool 2]:</strong> [Specific findings/scores]</li>
      <li><strong>[Assessment tool 3]:</strong> [Specific findings/scores]</li>
    </ul>

    <p>
      [Include relevant background information about current support services, family concerns, school observations, etc.]
    </p>

    <p>
      Observations and reports from [parents/teachers/school] highlight 
      [Patient's First Name]'s [specific areas of concern], suggesting that [he/she] would benefit from 
      [specific type of intervention/support]. Currently, [he/she] is receiving support from 
      [current services/professionals].
    </p>

    <p>
      Given these findings, I recommend [specific type of evaluation/intervention] to 
      achieve [specific goals/outcomes]. Your expertise in [relevant specialty area] will be 
      invaluable in [specific aspect of treatment planning/intervention].
    </p>

    <p>Thank you for your attention to this referral.</p>

    <p>Yours sincerely,</p>

    <p>
      [Your Title/Position]<br>
      [Your Name]<br>
      [Your Qualifications]<br>
      [Organization Name]
    </p>

    <p><em>This document is based solely on actual clinical findings and observations. Never fabricate assessment results, diagnostic conclusions, or professional recommendations.</em></p>
    `
    },
    {
    title: "Clinical Psychology Assessment Template",
    content:`
    <h2><strong>Student/Client Information:</strong></h2>
    <p>
      [Include case number, client's name, sex, date of birth, date of assessment, age in years and months as of assessment date, grade/educational level, school (if applicable), dates of assessment sessions, date of report, referral source, and any other relevant identifying information]
    </p>

    <h2><strong>Reasons for Referral:</strong></h2>
    <p>
      [Describe the specific concerns that prompted the referral, including who referred the client, the presenting problems or symptoms, developmental concerns, educational difficulties, behavioral issues, and the specific questions the assessment aims to address. Include any requests for diagnostic clarification, intervention planning, or special accommodations]
    </p>

    <h2><strong>Background Information:</strong></h2>
    <p>
      [Provide comprehensive background including: developmental history (prenatal, birth, early milestones), family background and structure, medical history (including any significant medical conditions, medications, hospitalizations), educational history and current functioning, social and emotional development, previous interventions or treatments, and any other relevant psychosocial factors]
    </p>

    <h2><strong>Previous Assessment Findings:</strong></h2>
    <p>
      [Summarize findings from previous psychological, educational, medical, or psychiatric evaluations, including assessor names, dates, organizations, diagnoses given, and key recommendations. Note consistency or discrepancies with current presentation] <br>
      <em>(Only include if explicitly mentioned in records; otherwise omit entirely)</em>
    </p>

    <h2><strong>Behavioral Observation:</strong></h2>
    <p>
      [Provide detailed observations during assessment sessions including: testing compliance and cooperation, attention span and concentration, motivation and effort level, social responsiveness and interaction style, communication abilities (speech, language, articulation), working style and approach to tasks, emotional presentation and mood, any off-task behaviors or notable characteristics. Conclude with statement about validity of assessment results based on observed behavior and cooperation level]
    </p>

    <h2><strong>Assessment Results:</strong></h2>

    <h3><em>Intellectual Functioning</em></h3>
    <p>
      [Detail cognitive assessment results including Full Scale IQ, index scores, subtest performance, and interpretation of cognitive profile]
    </p>

    <h3><em>Attention and Executive Functioning</em></h3>
    <p>
      [Present attention/concentration assessment results, including computerized tests if administered, with clinical interpretation]
    </p>

    <h3><em>Social and Adaptive Functioning</em></h3>
    <p>
      [Report adaptive behavior assessment results across conceptual, social, and practical domains, comparing ratings from multiple sources]
    </p>

    <h3><em>Emotional and Behavioral Functioning</em></h3>
    <p>
      [Include rating scale results from parents, teachers, and self-report measures, noting clinical vs. normal ranges and consistency across informants]
    </p>

    <h3><em>[Additional Domains as Assessed]</em></h3>
    <p>
      [Academic achievement, language, motor skills, etc.]
    </p>

    <h2><strong>Collateral Information:</strong></h2>
    <p>
      [Summarize information from parent and teacher rating scales, interviews, and observations, organizing by source and noting convergent findings across informants]
    </p>

    <h2><strong>Summary and Clinical Impressions:</strong></h2>
    <p>
      [Integrate all assessment findings to provide comprehensive understanding of the client's functioning. Address referral questions directly. Discuss diagnostic considerations, including criteria met or not met for specific conditions. Note any provisional diagnoses supported by the assessment data]
    </p>

    <h2><strong>Conclusion and Recommendations:</strong></h2>

    <h3><strong>Clinical Conclusion:</strong></h3>
    <p>
      [Provide summary statement of overall functioning level, key findings, and any provisional diagnoses supported by assessment data]
    </p>

    <h3><strong>Recommendations:</strong></h3>
    <ul>
      <li><strong>Referrals for Additional Services:</strong><br>
        [Specific referrals to medical, psychiatric, educational, or therapeutic services]</li>
      <li><strong>Educational/Academic Support:</strong><br>
        [Specific educational interventions, accommodations, or modifications]</li>
      <li><strong>Home and Family Support:</strong><br>
        [Recommendations for parents and family members]</li>
      <li><strong>Therapeutic Interventions:</strong><br>
        [Specific therapeutic approaches or interventions recommended]</li>
      <li><strong>Monitoring and Follow-up:</strong><br>
        [Recommendations for ongoing assessment, monitoring, or reassessment timelines]</li>
      <li><strong>Additional Considerations:</strong><br>
        [Any other relevant recommendations for optimal development and functioning]</li>
    </ul>

    <h2><strong>Psychologist Information:</strong></h2>
    <p>
      [Include psychologist's full name, professional title, license information, organization/clinic affiliation, and signature with date]
    </p>

    <h2><strong>Confidentiality Statement:</strong></h2>
    <p>
      [Note about the changing nature of child development and recommendations for current use, with caution advised for future reference of the report]
    </p>

    <hr>

    <p>
      <em>
        (Instructions: Never fabricate patient details, assessment results, diagnoses, or recommendations. Use only information explicitly provided in clinical notes, transcripts, or contextual materials. If information for any section is not available, leave blank or omit entirely. Ensure all scores, interpretations, and recommendations are directly supported by provided assessment data. Reference evidence-based practices only in recommendation sections.)
      </em>
    </p>
    `
    }, 
    {
    title: 'Insurance Claim Template',
    content: `
    <h2>1. PATIENT DETAILS</h2>
    <p><strong>Full Name (BLOCK LETTERS):</strong></p>
    <p><strong>Policy Number:</strong></p>
    <p><strong>Member/Certificate No.:</strong></p>
    <p><strong>Employer Name (if applicable):</strong></p>
    <p><strong>Age:</strong></p>
    <p><strong>Gender:</strong></p>
    <p><strong>ID Card Number:</strong></p>
    <p><strong>Mobile Number:</strong></p>
    <p><strong>Email:</strong></p>

    <hr>

    <h2>2. HOSPITALIZATION PARTICULARS</h2>
    <p><strong>Symptoms/Complaints:</strong></p>
    <p><strong>Symptoms Onset Date:</strong></p>
    <p><strong>Diagnosis:</strong></p>
    <p><strong>ICD-10 Code:</strong></p>
    <p><strong>Hospital Name:</strong></p>
    <p><strong>Admission Date:</strong></p>
    <p><strong>Discharge Date:</strong></p>
    <p><strong>Ward Level:</strong></p>
    <p><strong>Surgery Performed:</strong></p>
    <p><strong>Date of Surgery:</strong></p>
    <p><strong>Procedure:</strong></p>
    <p><strong>CPT Code:</strong></p>
    <p><strong>Surgeon Name:</strong> </p>

    <hr>

    <h2>3. CLINICAL HISTORY & PROFESSIONAL OPINION</h2>
    <p><strong>First Consultation Date:</strong></p>
    <p><strong>Duration of Symptoms Before Consultation:</strong></p>
    <p><strong>Clinical Findings:</strong></p>
    <p><strong>Final Diagnosis:</strong></p>
    <p><strong>Recurrent Episode/Chronic Illness:</strong></p>
    <p><strong>First Episode Date:</strong></p>
    <p><strong>Previous Treatment Details:</strong></p>

    <p><strong>Condition Related To:</strong></p>
    <ul>
      <li>Accidental Injury: [Yes/No]</li>
      <li>Pregnancy: [Yes/No]</li>
      <li>Congenital Condition: [Yes/No]</li>
      <li>Other (specify): [Other Cause]</li>
    </ul>

    <p><strong>Discharge Summary:</strong></p>
    <p><strong>Reason for Hospitalization (if outpatient care was possible):</strong></p>

    <hr>

    <h2>4. ATTENDING PHYSICIAN'S DECLARATION</h2>
    <p><strong>Physician's Name:</strong> </p>
    <p><strong>Qualifications:</strong> </p>
    <p><strong>Contact Number:</strong> </p>
    <p><strong>Clinic/Hospital Address:</strong> </p>
    <p><strong>Patient's Usual Physician:</strong> </p>
    <p><strong>Referring Doctor (if applicable):</strong> </p>
    <p><strong>Physician's Declaration:</strong></p>
      `
    },
    {
      title: 'Optical Character Recognition (OCR) Template',
      content: `
        <p>Recognize and transcribe all handwritings and images faithfully, in verbatim.</p>
        <br>
        <p>Display the original language of the handwritings.</p>
      `
    },
    {
      title: 'SOAP Note',
      content: `
        <p>(Summarize into the below format using bullet points)</p>
        <p><strong>Subjective: </strong>
        <br>
        <p><strong>Objective: </strong>
        <br>
        <p><strong>Assessment: </strong>
        <br>
        <p><strong>Plan: </strong>
      `
    }
];